import type { CaptionTrack, ControllerContext, Level } from "./index.js";
import { emitError } from "./index.js";
import { handleHlsError } from "./sentry-integration.js";

/**
 * HLS.js event callback types
 */
type HlsManifestParsedData = {
  levels: Array<{ height?: number; width?: number; bitrate?: number; name?: string }>;
};

type HlsSubtitleTrack = {
  id: number;
  lang?: string;
  name?: string;
  default?: boolean;
  forced?: boolean;
  url?: string; // VTT file URL
};

type HlsSubtitleTracksUpdatedData = {
  subtitleTracks: Array<HlsSubtitleTrack>;
};

/**
 * Load HLS stream using hls.js library.
 * Falls back to native video src if hls.js is not available or supported.
 *
 * @param onLevelsLoaded - Callback when quality levels are parsed from manifest
 * @param onSubtitlesLoaded - Callback when subtitle tracks are loaded from manifest
 */
export async function loadWithHls(
  ctx: ControllerContext,
  v: HTMLVideoElement,
  url: string,
  onLevelsLoaded?: (levels: Level[]) => void,
  onSubtitlesLoaded?: (captions: CaptionTrack[]) => void,
): Promise<{ destroy(): void } | null> {
  try {
    const mod: unknown = await import("hls.js");
    const maybeDefault = mod as { default?: unknown; Events?: Record<string, string> };
    const Ctor = (maybeDefault.default ?? mod) as {
      isSupported: () => boolean;
      Events?: Record<string, string>;
      new (): {
        attachMedia(m: HTMLMediaElement): void;
        loadSource(u: string): void;
        on(evt: string, cb: (...args: unknown[]) => void): void;
        destroy(): void;
        levels?: Array<{ height?: number; width?: number; bitrate?: number; name?: string }>;
      };
    };

    // Access HLS.js Events enum
    const HlsEvents = maybeDefault.Events ?? (mod as { Events?: Record<string, string> }).Events;

    if (typeof Ctor.isSupported === "function" && Ctor.isSupported()) {
      // Create HLS.js instance with subtitle rendering configuration
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - constructor typing narrowed above
      const instance = new (Ctor as unknown as new (config?: { renderTextTracksNatively?: boolean }) => {
        attachMedia(m: HTMLMediaElement): void;
        loadSource(u: string): void;
        on(evt: string, cb: (...args: unknown[]) => void): void;
        destroy(): void;
        levels?: Array<{ height?: number; width?: number; bitrate?: number; name?: string }>;
      })({
        // Let HLS.js render subtitles natively through video element
        // This allows our CSS ::cue styling to work properly
        renderTextTracksNatively: true,
      });

      instance.attachMedia(v);
      instance.loadSource(url);

      // Listen for error events
      instance.on("hlsError", (err: unknown) => {
        // Enhanced HLS error handling with Sentry integration
        handleHlsError(err as { type?: string; details?: string; fatal?: boolean }, {
          assetId: ctx.progress.assetId || undefined,
          titleId: ctx.titleId || undefined,
          video: v,
        });
        emitError(ctx, err);
      });

      // Listen for manifest parsed to extract quality levels
      if (onLevelsLoaded) {
        const manifestEvent = HlsEvents?.MANIFEST_PARSED ?? "manifestParsed";
        instance.on(manifestEvent, (...args: unknown[]) => {
          const data = args[1] as HlsManifestParsedData | undefined;
          const hlsLevels = data?.levels || instance.levels || [];
          const levels: Level[] = hlsLevels.map((level, idx) => ({
            id: idx,
            label: level.height ? `${level.height}p` : level.name || `Level ${idx}`,
            bitrate: level.bitrate,
            width: level.width,
            height: level.height,
            isAuto: false,
          }));

          // Add "Auto" level at the beginning
          if (levels.length > 0) {
            levels.unshift({ id: -1, label: "Auto", isAuto: true });
          }

          onLevelsLoaded(levels);
        });
      }

      // Listen for subtitle tracks from manifest
      if (onSubtitlesLoaded) {
        const subtitleEvent = HlsEvents?.SUBTITLE_TRACKS_UPDATED ?? "subtitleTracksUpdated";
        let lastTracksHash = "";

        instance.on(subtitleEvent, (...args: unknown[]) => {
          const data = args[1] as HlsSubtitleTracksUpdatedData | undefined;
          const tracks = data?.subtitleTracks || [];

          // Deduplicate: compute hash of tracks to skip redundant events
          const tracksHash = tracks.map((t) => `${t.id}:${t.lang}`).join(",");
          if (tracksHash === lastTracksHash) {
            return;
          }
          lastTracksHash = tracksHash;

          const captions: CaptionTrack[] = tracks.map((track) => ({
            id: track.id,
            lang: track.lang || "unknown",
            label: track.name || track.lang || `Track ${track.id}`,
            forced: track.forced,
            url: track.url, // Pass through VTT URL if available
          }));

          onSubtitlesLoaded(captions);
        });
      }

      return instance as unknown as { destroy(): void };
    }
    // Not supported — fallback to plain src
    (v as unknown as { src?: string }).src = url;
    return null;
  } catch (e) {
    // hls.js not available — fallback to plain src
    try {
      (v as unknown as { src?: string }).src = url;
    } catch {
      // ignore
    }
    emitError(ctx, e);
    return null;
  }
}

/**
 * Check if the browser can natively play HLS streams.
 * Only Safari has complete HLS support including subtitle tracks from manifest.
 * Chrome claims support but doesn't parse subtitles, so we force HLS.js for non-Safari browsers.
 */
export function canPlayNativeHls(v: HTMLVideoElement): boolean {
  // Guard against undefined navigator (Node.js test environment)
  if (typeof navigator === "undefined") {
    return false;
  }

  // Check if browser is Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  return (
    isSafari &&
    typeof v.canPlayType === "function" &&
    (v.canPlayType("application/vnd.apple.mpegurl") !== "" || v.canPlayType("application/x-mpegURL") !== "")
  );
}
